import GoogleMapReact, { Coords, Props as TGoogleMapProps } from 'google-map-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSupercluster from 'use-supercluster'

import { ClusterMarker } from './ClusterMarker'
import { SimpleMarker } from './SimpleMarker'

export type TLocalization = { id: number } & Coords

export type GMapProps = {
  membersLocation: TLocalization[]
  center?: Coords
  zoom?: number
  onMarkerClick?: (clickedCoords: Coords) => void
}

const DEFAULT_ZOOM = 4
const DEFAULT_CENTER_COORDS = { lat: 46.1279017, lng: 2.4361634 }

export const GMap: TFC<GMapProps> = ({ membersLocation, center, zoom, onMarkerClick }) => {
  const [bounds, setBounds] = useState<[number, number, number, number]>(null)
  const [currentZoom, setCurrentZoom] = useState<number>(zoom ?? DEFAULT_ZOOM)
  const {
    i18n: { language },
  } = useTranslation()

  const locations: TLocalization[] = membersLocation ? membersLocation.slice(0, 2000) : []
  const points = locations.map((location, i) => ({
    type: 'Feature',
    properties: { cluster: false, locationId: location.id || i },
    geometry: {
      type: 'Point',
      coordinates: [location.lng, location.lat],
    },
  }))

  const { clusters } = useSupercluster({
    points,
    bounds,
    zoom: currentZoom,
    options: { radius: 75, maxZoom: 20 },
  })

  const onChange: TGoogleMapProps['onChange'] = ({ zoom: newZoom, bounds: newBounds }) => {
    setCurrentZoom(newZoom)
    setBounds([newBounds.nw.lng, newBounds.se.lat, newBounds.se.lng, newBounds.nw.lat])
  }

  useEffect(() => {
    setCurrentZoom(zoom ?? DEFAULT_ZOOM)
  }, [zoom])

  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        key: CONF_KEYS.GMAPS_API_KEY,
        libraries: 'places',
        language: language,
      }}
      defaultCenter={DEFAULT_CENTER_COORDS}
      defaultZoom={DEFAULT_ZOOM}
      onChange={onChange}
      zoom={currentZoom}
      center={center ?? DEFAULT_CENTER_COORDS}
    >
      {clusters?.map((cluster, i) => {
        const [lng, lat] = cluster.geometry.coordinates
        const { cluster: isCluster, point_count: pointCount } = cluster.properties

        return isCluster ? (
          <ClusterMarker key={i} lat={lat} lng={lng} pointCount={pointCount} nbPoints={points.length} />
        ) : (
          <SimpleMarker key={i} lat={lat} lng={lng} onClick={onMarkerClick} />
        )
      })}
    </GoogleMapReact>
  )
}
